'use client'

import { cn } from '@/utils/common'
import { cva, type VariantProps } from 'class-variance-authority'
import { ReactNode, HTMLAttributes, useState } from 'react'

export const typographyVariants = cva('', {
  variants: {
    variant: {
      h0: 'text-5xl',
      h1: 'text-4xl',
      h2: 'text-3xl',
      h3: 'text-2xl',
      h4: 'text-xl',
      h5: 'text-lg',
      h6: 'text-base',
      paragraph: 'text-sm',
      small: 'text-xs',
      subTitle: 'text-[11px]',
      verySmall: 'text-[8px]',
    },
    weight: {
      thin: 'font-thin',
      extraLight: 'font-extralight',
      light: 'font-light',
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      extrabold: 'font-extrabold',
      black: 'font-black',
      extraBlack: 'font-extrablack',
      heavy: 'font-heavy',
    },
  },
  defaultVariants: {
    variant: 'paragraph',
    weight: 'normal',
  },
})

export interface TypographyProps
  extends HTMLAttributes<HTMLParagraphElement>,
    VariantProps<typeof typographyVariants> {
  children: ReactNode
  className?: string
  center?: boolean
  limit?: number
  noResume?: boolean
}

const Typography: React.FC<TypographyProps> = ({
  variant,
  weight,
  children,
  className,
  center,
  limit,
  onClick,
  noResume = false,
  ...props
}) => {
  const [limitation, setLimitation] = useState(limit)
  const handleClick = () => setLimitation(s => (s ? undefined : limit))

  const formattedChildren =
    typeof children === 'number' ? children.toLocaleString() : children

  return (
    <p
      className={cn(
        typographyVariants({ variant, weight, className }),
        className,
        {
          'text-center': center,
        }
      )}
      onClick={e => {
        handleClick()
        onClick?.(e)
      }}
      {...props}
    >
      {formattedChildren}
    </p>
  )
}

export default Typography
